import { Field } from "@/components/atoms/Field"
import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
  Stack,
  Textarea,
} from "@chakra-ui/react"
import { memo, useEffect } from "react"
import { useForm } from "react-hook-form"

export interface RejectPaymentFormData {
  rejection_reason: string
  rejection_notes: string
}

interface RejectPaymentModalProps {
  isOpen: boolean
  invoiceId: string | null
  onClose: () => void
  onSubmit: (invoiceId: string, data: RejectPaymentFormData) => Promise<void>
  isSubmitting?: boolean
}

const REJECTION_REASON_OPTIONS = [
  { value: "transaction_not_found", label: "Transaction not found" },
  { value: "amount_mismatch", label: "Amount mismatch" },
  { value: "duplicate_submission", label: "Duplicate submission" },
  { value: "other", label: "Other" },
]

const RejectPaymentModal = memo(
  ({
    isOpen,
    invoiceId,
    onClose,
    onSubmit,
    isSubmitting = false,
  }: RejectPaymentModalProps) => {
    const form = useForm<RejectPaymentFormData>({
      defaultValues: {
        rejection_reason: "",
        rejection_notes: "",
      },
    })

    useEffect(() => {
      if (isOpen) {
        form.reset({
          rejection_reason: "",
          rejection_notes: "",
        })
      }
    }, [isOpen, form])

    const handleSubmit = form.handleSubmit(async (data) => {
      if (!invoiceId) return
      await onSubmit(invoiceId, data)
      form.reset()
    })

    if (!invoiceId) return null

    return (
      <Portal>
        <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reject Payment Confirmation</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody>
                <form onSubmit={handleSubmit}>
                  <Stack gap={4}>
                    <Field
                      label="Rejection Reason"
                      required
                      invalid={!!form.formState.errors.rejection_reason}
                      errorText={
                        form.formState.errors.rejection_reason?.message
                      }
                    >
                      <select
                        {...form.register("rejection_reason", {
                          required: "Please select a rejection reason",
                        })}
                        className="chakra-select"
                      >
                        <option value="">Select a reason...</option>
                        {REJECTION_REASON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field
                      label="Additional Notes"
                      helperText="Provide any additional details for the parent (optional)"
                      invalid={!!form.formState.errors.rejection_notes}
                      errorText={form.formState.errors.rejection_notes?.message}
                    >
                      <Textarea
                        placeholder="Enter additional notes..."
                        rows={3}
                        {...form.register("rejection_notes")}
                      />
                    </Field>
                  </Stack>
                </form>
              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={onClose} mr={3}>
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  loadingText="Rejecting..."
                >
                  Reject
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </DialogRoot>
      </Portal>
    )
  },
)

RejectPaymentModal.displayName = "RejectPaymentModal"

export default RejectPaymentModal
